import { useEffect, useState } from 'preact/hooks';
import type { Connection } from '../../types';
import { connectionManagementService } from '../../services/connections/connection-management.service';
import { MainLayout } from '../../components/MainLayout';
import { useScopeContext } from '../../contexts/ScopeContext';

export function ConnectionsPage() {
  const scope = useScopeContext();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConnections();
  }, [scope.teamId]); // Reload when team context changes

  const loadConnections = async () => {
    try {
      setLoading(true);
      const data = await connectionManagementService.getAllConnections(scope.teamId);
      console.log('Connections data:', data); // Debug log
      setConnections(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="container mx-auto p-4">
            <div className="alert alert-error">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">
      <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            Team Connections
          </h1>
          <p className="text-base-content/70 mt-1">Shared connections for the team</p>
        </div>
        <a href={`${scope.getNavigationPrefix()}/connectors`} className="btn btn-primary">
          Browse Connectors
        </a>
      </div>

      {connections.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-lg opacity-60 mb-4">
            No team connections yet
          </p>
          <a href={`${scope.getNavigationPrefix()}/connectors`} className="btn btn-primary">
            Browse Available Connectors
          </a>
        </div>
      ) : (
        <div className="grid gap-4">
          {connections.map((connection) => {
            const connectionId = connection.connectionId;
            const connectionName = connection.metadata?.originalName || connection.connectorId;
            return (
            <a
              key={connectionId}
              href={`${scope.getNavigationPrefix()}/connections/${connectionId}`}
              className="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow cursor-pointer block"
            >
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      {connection.connector?.icon?.content && (
                        <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center">
                          {connection.connector.icon.content.startsWith('<svg') ? (
                            <div className="w-6 h-6" dangerouslySetInnerHTML={{ __html: connection.connector.icon.content }} />
                          ) : (
                            <span className="text-xl">{connection.connector.icon.content}</span>
                          )}
                        </div>
                      )}
                      <h3 className="card-title">{connectionName}</h3>
                    </div>
                    <p className="text-sm opacity-70">
                      Connector: {connection.connectorId}
                    </p>
                    {connection.metadata?.description && (
                      <p className="text-sm mt-1">{connection.metadata.description}</p>
                    )}
                    <div className="flex gap-2 mt-2">
                      <span className={`badge ${connection.hasCredentials ? 'badge-success' : 'badge-ghost'}`}>
                        {connection.hasCredentials ? 'configured' : 'not_configured'}
                      </span>
                      {connection.active && (
                        <span className="badge badge-info">Active</span>
                      )}
                    </div>
                  </div>

                  <div className="text-2xl opacity-50">
                    →
                  </div>
                </div>

              </div>
            </a>
            );
          })}
        </div>
      )}
      </div>
      </div>
    </MainLayout>
  );
}
